﻿import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Layout.css';
import classNames from 'classnames';

import { UserAccess, UserHasAccessActions } from 'components/user-access';
import { InternalMenuItem } from 'components/navigation';

import { useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';

import menuJson from './LeftMenu.json';
import Cookies from 'js-cookie';
import { lightenColourTowardsWhite } from 'utils/colourUtils';

export function LeftMenu() {

    const [menuItems, setMenuItems] = useState([]);
    let location = useLocation();
    const userActions = useSelector(state => state.loggedInUser.actions);
    const sidebarColour = useSelector(state => state.theme.sidebarColour);
    const sidebarColourPreview = useSelector(state => state.theme.sidebarColourPreview);
    const sidebarActiveMenuColour = useSelector(state => state.theme.sidebarActiveMenuColour);
    const sidebarActiveMenuColourPreview = useSelector(state => state.theme.sidebarActiveMenuColourPreview);
    const sidebarTextColour = useSelector(state => state.theme.sidebarTextColour);
    const sidebarTextColourPreview = useSelector(state => state.theme.sidebarTextColourPreview);
    const [topLocation, setTopLocation] = useState('');
    const [fullLocation, setFullLocation] = useState('');
    const menuRefs = useRef({});
    const handlerRefs = useRef({});
    const leftBarStyleRef = useRef(null);

    useEffect(function () {
        if (location != null && location.pathname) {
            setFullLocation(location.pathname);
            var urlSections = location.pathname.split('/');
            setTopLocation('/' + urlSections[1]);
        }
    }, [location]);

    useEffect(function () {
        parseSitePrefix(menuJson);
    }, []);

    useEffect(parseMenu, [userActions, menuJson]);
    useEffect(parseActiveStates, [topLocation]);

    // Adjust submenu position on hover to keep it within viewport
    const handleMouseEnter = useCallback((id) => {
        const ref = menuRefs.current[id];
        if (ref) {
            const rect = ref.getBoundingClientRect();
            const popout = ref.querySelector('.menu-popout');
            if (popout) {
                popout.style.top = '0px';
                const popoutRect = popout.getBoundingClientRect();
                const overflow = (popoutRect.bottom > window.innerHeight);
                if (overflow) {
                    let offset = window.innerHeight - popoutRect.bottom;
                    // If popout is taller than viewport, align to top
                    if (popoutRect.height > window.innerHeight) {
                        popout.style.top = `${-rect.top}px`;
                    } else {
                        popout.style.top = `${offset}px`;
                    }
                }
            }
        }
    }, []);

    useEffect(() => {
        menuItems.forEach(menuItem => {
            const ref = menuRefs.current[menuItem.id];
            if (ref) {
                if (!handlerRefs.current[menuItem.id]) {
                    handlerRefs.current[menuItem.id] = {
                        enter: () => handleMouseEnter(menuItem.id)
                    };
                }
                ref.addEventListener('mouseenter', handlerRefs.current[menuItem.id].enter);
            }
        });
        
        return () => {
            menuItems.forEach(menuItem => {
                const ref = menuRefs.current[menuItem.id];
                const handlers = handlerRefs.current[menuItem.id];
                if (ref && handlers) {
                    ref.removeEventListener('mouseenter', handlers.enter);
                }
            });
        };
    }, [menuItems, handleMouseEnter]);
    
    useEffect(() => {
        const styleId = 'dynamic-left-bar';
        const hoverAndPopupLightenBy = 0.2;
        let styleTag = leftBarStyleRef.current;

        const bg = sidebarColourPreview || sidebarColour || "#0C2235";
        const activeBgColour = sidebarActiveMenuColourPreview || sidebarActiveMenuColour || "#008CFF";
        const textColour = sidebarTextColourPreview || sidebarTextColour || "#ffffff";
        const hoverAndPopoutColour = lightenColourTowardsWhite(bg, hoverAndPopupLightenBy);
        const css = `
            #left-bar {
                background: ${bg} !important;
            }
                #left-bar ul li.active, #left-bar ul li.active.external {
                    background-color: ${activeBgColour} !important;
                }
                #left-bar ul li:hover,  #left-bar ul li .menu-popout {
                    background: ${hoverAndPopoutColour} !important;
                }
                    #left-bar ul li .menu-icon .custom-icon, #left-bar ul li .menu-icon .fa {
                        color: ${textColour} !important;
                    }
                #left-bar ul li.external .menu-icon .custom-icon, #left-bar ul li.external .menu-icon .fa {
                    color: ${activeBgColour} !important;
                }
                #left-bar ul li.external.active .menu-icon .custom-icon, #left-bar ul li.external.active .menu-icon .fa {
                    color: ${textColour} !important;
                }
                #left-bar ul li .menu-popout h4 {
                    color: ${textColour} !important;
                }
                #left-bar ul li .menu-popout ul li a {
                    color: ${textColour} !important;
                }

                    #left-bar ul li .menu-popout ul li a:hover {
                        background: ${bg} !important;
                    }
        `;

        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = styleId;
            document.head.appendChild(styleTag);
            leftBarStyleRef.current = styleTag;
        }
        styleTag.innerHTML = css;

        return () => {
            removeLeftBarStyles();
        };
    }, [sidebarColour, sidebarColourPreview, sidebarTextColour, sidebarTextColourPreview, sidebarActiveMenuColour, sidebarActiveMenuColourPreview]);

    function removeLeftBarStyles() {
        if (leftBarStyleRef.current) {
            document.head.removeChild(leftBarStyleRef.current);
            leftBarStyleRef.current = null;
        }
    }

    function checkForCookie(cookieName) {
        return Cookies.get(cookieName) !== undefined;
    }

    function parseMenu() {
        if (userActions) {
            setMenuItems(menuJson);
        }
    }

    function getPermittedChildren(children) {
        return children.filter(child => {
            return UserHasAccessActions(child.role, userActions)
        });
    }

    function parseActiveStates() {
        if (menuItems.length === 0) {
            return;
        }

        const items = [
            ...menuItems
        ];

        for (let item of items) {
            item.active = (item.url === topLocation || item.url === fullLocation || (topLocation === item.homeLocation));

            if (item.children != null) {
                const children = getPermittedChildren(item.children);

                if (!item.active) {
                    //Check to see if any of the children are active
                    item.active = areChildrenActive(children)
                }

                if (children.length === 1) {
                    let tempName = item.name
      
                    if (!children[0].useParentNameWhenSingle && children[0].name){
                        tempName = children[0].name;
                    }
            
                    //Need to make this the top level
                    item.name = tempName;
                    item.icon = children[0].icon ? children[0].icon : item.icon;
                    item.url = children[0].url ? children[0].url : item.url;
                    item.role = children[0].role ? children[0].role : item.role;
                    item.sitePrefix = children[0].sitePrefix ? children[0].sitePrefix : item.sitePrefix;
                    item.id = children[0].id ? children[0].id : item.id;
                    item.children = null;
                }
            }
        }
        setMenuItems(items);
    }

    function parseSitePrefix(items) {
        for (let item of items) {
            if (item.sitePrefix != null && !item.prefixApplied) {
                let prefix = "";

                if (item.newUrl && item.cookieName && checkForCookie(item.cookieName)) {
                    item.url = item.newUrl;
                    item.sitePrefix = null;
                }

                if (item.sitePrefix === "vision" && (!item.cookieName || (item.cookieName && !checkForCookie(item.cookieName)))) {
                    prefix = window.env.VISION_URL;
                } else if (item.sitePrefix === "memberadmin") {
                    prefix = window.env.VISIONMEMBER_URL;
                } else if (item.sitePrefix === "pcn") {
                    prefix = window.env.PCN_URL;
                }

                item.url = prefix + item.url;

                item.forceRedirect = prefix !== "";
                item.prefixApplied = true;
            }

            if (item.children != null) {
                parseSitePrefix(item.children);
            }
        }
    }

    function areChildrenActive(items) {
        for (let item of items) {
            if (item.url === topLocation || item.url === fullLocation) {
                return true;
            }
        }

        return false;
    }

    return (
        <div id="left-bar">
            <ul>
                {menuItems.map(function (menuItem) {
                    return (
                        <UserAccess key={menuItem.name} perform={menuItem.role}
                            yes={() => {
                                if (menuItem.children != null) {
                                    return (
                                        <li ref={el => menuRefs.current[menuItem.id] = el} className={classNames({ "active": menuItem.active, "external": menuItem.external })}><span className="menu-icon"><i className={menuItem.icon} style={menuItem.iconStyle} id={menuItem.id} /></span>
                                            <div className="menu-popout">
                                                <h4>{menuItem.name}</h4>
                                                {!menuItem.children ? null : (
                                                    <ul>
                                                        {menuItem.children.map(function (childItem) {
                                                            return (
                                                                <UserAccess key={childItem.name} perform={childItem.role}
                                                                    yes={() => (
                                                                        <InternalMenuItem
                                                                            forceRedirect={childItem.sitePrefix == null ? false : true}
                                                                            text={childItem.name}
                                                                            location={childItem.url}
                                                                            id={childItem.id}
                                                                        />
                                                                    )}
                                                                />
                                                            );
                                                        })}
                                                    </ul>
                                                )}

                                            </div>
                                        </li>
                                    )
                                } else {
                                    return (
                                        <li className={classNames({ "active": menuItem.active, "external": menuItem.external })}>
                                            <InternalMenuItem nodeType="a" forceRedirect={menuItem.sitePrefix != null} text={(<i className={menuItem.icon} style={menuItem.iconStyle} />)} location={menuItem.url} className="menu-icon" id={menuItem.id} />
                                            <div className="menu-popout">
                                                <h4>{menuItem.name}</h4>
                                            </div>
                                        </li>
                                    );
                                }
                            }}
                        />
                    );
                })}
            </ul>
        </div>
    );
}

